import cleanObject from '../../newInterstitials/utilities/cleanObject'
import convertLocale from '../../newInterstitials/utilities/convertLocale'

// The Web SDK replaces a popup it controls within a second or two (the start call itself takes
// well under a second), so a popup still showing the loader after this is one no SDK controls.
export const sdkGraceMs = 5000
export const startTimeoutMs = 15000
export const defaultLoaderDelayMs = 60000

export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (error) {
    // Some sandboxed frames throw on window.top access; those are frames too.
    return true
  }
}

/**
 * The SDK can only control a window it opened with window.open, which always sets an opener.
 * A popup the browser opens later from its "pop-up blocked" prompt may have none.
 */
export function hasOpener(): boolean {
  return !!window.opener
}

/** The user came back to this page with Back, Forward or reload, after the SDK had replaced it. */
export function isReturnVisit(): boolean {
  const [entry] = performance.getEntriesByType?.('navigation') ?? []
  const type = (entry as PerformanceNavigationTiming | undefined)?.type
  return type === 'back_forward' || type === 'reload'
}

export type ProgrammaticPrequalificationParams = {
  /** CMA base URL for the locale's region, e.g. https://js.klarna.com/na/cma */
  baseUrl: string
  /** Loader locale, e.g. en_US */
  locale: string
  purchaseAmount?: number
  currency?: string
  clientId?: string
  integratorOrigin?: string
  accountId?: string
}

type PrequalificationStartResponse = {
  prequalification_client_uri?: string | null
}

async function fetchClientUri(url: string, headers: Record<string, string>, signal: AbortSignal) {
  const response = await fetch(url, { headers, signal })
  // A 400 still carries a JSON body with a null URI, so read the body before checking the status.
  const data: PrequalificationStartResponse = await response.json().catch((): PrequalificationStartResponse => ({}))
  const clientUri = data?.prequalification_client_uri

  if (!response.ok || !clientUri) {
    throw new Error(`Prequalification start failed with status ${response.status}`)
  }
  return clientUri
}

/**
 * Starts the flow the way the Web SDK does (cma v2 start, authenticated with the merchant's
 * client id and origin) and replaces this page with it. Throws if the flow can't be started.
 */
export async function startProgrammaticPrequalification({
  baseUrl,
  locale,
  purchaseAmount,
  currency,
  clientId,
  integratorOrigin,
  accountId,
}: ProgrammaticPrequalificationParams): Promise<void> {
  if (!clientId || !integratorOrigin) {
    throw new Error('Missing clientId or integratorOrigin for the prequalification start call')
  }

  const queryString = new URLSearchParams(cleanObject({
    locale: convertLocale(locale),
    currency,
    purchase_amount: purchaseAmount,
    create_shopping_session: 'true',
    source: 'WEB_SDK',
  })).toString()

  const headers: Record<string, string> = {
    Authorization: `basic ${clientId}`,
    'Klarna-Integrator-Origin': integratorOrigin,
  }
  if (accountId) {
    headers['Klarna-Account'] = accountId
  }

  // A stalled request would otherwise leave the page loading forever.
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), startTimeoutMs)
  let clientUri: string
  try {
    clientUri = await fetchClientUri(`${baseUrl}/v2/prequalification/start?${queryString}`, headers, controller.signal)
  } catch (error) {
    throw controller.signal.aborted ? new Error(`Prequalification start timed out after ${startTimeoutMs}ms`) : error
  } finally {
    clearTimeout(timeoutId)
  }

  const url = new URL(clientUri)
  if (url.protocol !== 'https:') {
    throw new Error('Prequalification start returned a non-https client URI')
  }
  url.searchParams.set('locale', locale)
  // The page is a popup, so the flow's close button can close it with window.close().
  url.searchParams.set('show_close_button', 'true')
  window.location.replace(url.toString())
}
